import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Avatar,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Badge,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import XIcon from "@mui/icons-material/X";
import InstagramIcon from "@mui/icons-material/Instagram";
import SportsIcon from "@mui/icons-material/Sports";
import useBoardSelection from "../../../../hooks/useBoardSelection";
import BoardSelectionMenus from "../../../../components/common/BoardSelectionMenus";

const ProfileHeader = ({
  athlete,
  onBack,
  onAddToBoard,
  onUpdateAthlete,
  lastVisited,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  // Use the board selection hook
  const boardSelection = useBoardSelection();

  const handleBoardAddSuccess = (boardId, columnId, boardName, columnName) => {
    if (onAddToBoard) {
      onAddToBoard(boardId, columnId, boardName, columnName);
    }
  };

  const handleNavigateToBoard = (boardId) => {
    navigate(`/boards?boardId=${boardId}`);
  };

  // Handle social media links
  const handleTwitterClick = () => {
    const twitterHandle = athlete.socialMedia?.twitter;
    if (twitterHandle) {
      // Remove @ if present and construct URL
      const handle = twitterHandle.startsWith("@")
        ? twitterHandle.substring(1)
        : twitterHandle;
      const twitterUrl = `https://twitter.com/${handle}`;
      window.open(twitterUrl, "_blank");
    }
  };

  const handleHuddleClick = () => {
    const huddleLinks = athlete.socialMedia?.hudl;
    if (huddleLinks && huddleLinks.length > 0) {
      // Open the first Huddle link
      window.open(huddleLinks[0], "_blank");
    }
  };

  const handleInstagramClick = () => {
    const instagramHandle = athlete.socialMedia?.instagram;
    if (instagramHandle) {
      // Remove @ if present and construct URL
      const handle = instagramHandle.startsWith("@")
        ? instagramHandle.substring(1)
        : instagramHandle;
      const instagramUrl = `https://instagram.com/${handle}`;
      window.open(instagramUrl, "_blank");
    }
  };

  // Render social media links
  const renderSocialLinks = () => {
    const hasTwitter = athlete.socialMedia?.twitter;
    const hasInstagram = athlete.socialMedia?.instagram;
    const hasHuddle =
      athlete.socialMedia?.hudl && athlete.socialMedia.hudl.length > 0;

    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {hasTwitter && (
          <Tooltip title="View on X (Twitter)">
            <IconButton
              size="small"
              onClick={handleTwitterClick}
              sx={{
                color: "text.secondary",
                "&:hover": { color: "#1DA1F2" },
              }}
            >
              <XIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {hasInstagram && (
          <Tooltip title="View on Instagram">
            <IconButton
              size="small"
              onClick={handleInstagramClick}
              sx={{
                color: "text.secondary",
                "&:hover": { color: "#E4405F" },
              }}
            >
              <InstagramIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {hasHuddle && (
          <Tooltip title="View on Huddle">
            <IconButton
              size="small"
              onClick={handleHuddleClick}
              sx={{
                color: "text.secondary",
                "&:hover": { color: "#00A3E0" },
              }}
            >
              <SportsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {!hasTwitter && !hasInstagram && !hasHuddle && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontStyle: "italic" }}
          >
            No social links available
          </Typography>
        )}
      </Box>
    );
  };

  // Format position with secondary position
  const formattedPosition = athlete.secondaryPosition
    ? `${athlete.position || "N/A"} / ${athlete.secondaryPosition}`
    : athlete.position || "N/A";

  return (
    <Box sx={{ mb: 2, pt: 2 }}>
      {/* Back button and last visited */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          variant="text"
          size="small"
        >
          Back to Prospects
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<PersonAddIcon />}
          onClick={boardSelection.handleBoardMenuClick}
          size={isMobile ? "small" : "medium"}
        >
          Add to Board
        </Button>
      </Box>

      {/* Main header content */}
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "center" : "flex-start",
          gap: 3,
        }}
      >
        {/* Avatar with status indicator */}
        <Badge
          overlap="circular"
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          badgeContent={
            <Tooltip title={athlete.recruitingStatus || "Unknown"}>
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: "50%",
                  bgcolor:
                    athlete.recruitingStatus === "Hot" ||
                    athlete.recruitingStatus === "committed"
                      ? "error.main"
                      : athlete.recruitingStatus === "Warm" ||
                        athlete.recruitingStatus === "interested"
                      ? "warning.main"
                      : athlete.recruitingStatus === "Cold" ||
                        athlete.recruitingStatus === "contacted"
                      ? "info.main"
                      : "grey.500",
                  border: "2px solid white",
                }}
              />
            </Tooltip>
          }
        >
          <Avatar
            src={athlete.profileImage}
            alt={`${athlete.firstName || ""} ${athlete.lastName || ""}`}
            sx={{
              width: isMobile ? 100 : 120,
              height: isMobile ? 100 : 120,
              border: `3px solid ${theme.palette.primary.main}`,
            }}
          />
        </Badge>

        {/* Athlete info */}
        <Box
          sx={{
            flex: 1,
            textAlign: isMobile ? "center" : "left",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 0.5,
              justifyContent: isMobile ? "center" : "flex-start",
            }}
          >
            <Typography variant="h4" component="h1" sx={{ fontWeight: "bold" }}>
              {athlete.firstName || "Unknown"} {athlete.lastName || "Athlete"}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              mb: 1,
              justifyContent: isMobile ? "center" : "flex-start",
            }}
          >
            <Chip
              label={formattedPosition}
              color="primary"
              size="small"
              variant="outlined"
            />
            <Chip
              label={`Class of ${athlete.graduationYear || "N/A"}`}
              size="small"
              variant="outlined"
            />
            <Chip
              label={athlete.school || athlete.highSchool || "N/A"}
              size="small"
              variant="outlined"
            />
            <Chip
              label={`${athlete.city || athlete.address?.city || "N/A"}, ${
                athlete.state || athlete.address?.state || "N/A"
              }`}
              size="small"
              variant="outlined"
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 1,
              justifyContent: isMobile ? "center" : "flex-start",
            }}
          >
            {renderSocialLinks()}
          </Box>

          <Typography variant="body2" color="text.secondary">
            {athlete.height || "N/A"} • {athlete.weight || "N/A"} •{" "}
            {athlete.age || "N/A"} years old
          </Typography>
        </Box>

        {/* Action buttons */}
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "row" : "column",
            gap: isMobile ? 1 : 1.5,
            flexWrap: isMobile ? "wrap" : "nowrap",
            justifyContent: isMobile ? "center" : "flex-start",
          }}
        >
          {/* <IconButton
            aria-label="more options"
            aria-controls={open ? "profile-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
            onClick={handleMenuClick}
            sx={{ alignSelf: isMobile ? "center" : "flex-end" }}
          >
            <MoreVertIcon />
          </IconButton> */}
        </Box>
      </Box>

      {/* More options menu
      <Menu
        id="profile-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        MenuListProps={{
          "aria-labelledby": "more-button",
        }}
      >
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Profile</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon>
            <CompareArrowsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Compare Athletes</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon>
            <ShareIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Share Profile</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon>
            <PrintIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Print Profile</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export Data</ListItemText>
        </MenuItem>
      </Menu> */}

      {/* Board Selection Menus */}
      <BoardSelectionMenus
        {...boardSelection}
        athlete={athlete}
        onAddToBoard={(athlete, boardId, columnId) =>
          boardSelection.handleAddToBoard(
            athlete,
            boardId,
            columnId,
            handleBoardAddSuccess
          )
        }
        onCreateBoard={(athlete) =>
          boardSelection.handleCreateBoard(athlete, handleBoardAddSuccess)
        }
        onNavigateToBoard={handleNavigateToBoard}
      />
    </Box>
  );
};

export default ProfileHeader;
